import { useEffect, useRef, useState, type CSSProperties } from 'react';

/**
 * A text input that holds a LOCAL draft while it is being edited and reports
 * the value ONCE, when the edit is finished.
 *
 * #688 — the three Registrant Information fields on the case Overview tab used
 * to bind `value` straight to server state and call `updateCase` from
 * `onChange`, so every keystroke was a full `PATCH /api/v1/cases/:id`. Two
 * things go wrong with that, and both were reproduced against the real
 * AppDataContext chain before this component existed:
 *
 *   * The field cannot show a character until its PATCH round-trips. Typing
 *     faster than the round trip means each resolution overwrites the input
 *     with a server value that is several characters behind, so characters are
 *     dropped and the caret jumps. Typing "Acme Agent" at 25ms/char against a
 *     150ms server left "A A" in the database.
 *   * A keystroke that lands after a PATCH resolves but before React commits
 *     the re-render reads the pre-PATCH version out of the `updateCase`
 *     closure, forks a SECOND If-Match chain, and 409s — one refetch and one
 *     "Another user updated this case" toast per fork.
 *
 * Committing on blur / Enter is the repo's "one PATCH per logical action" rule
 * (see the same-tick serialization note in CLAUDE.md), and it is the pattern
 * OnboardingTab's `drafts` map already uses for its data fields.
 *
 * `onCommit` fires only when the value actually changed, so tabbing through a
 * field sends nothing.
 */
export interface DraftTextInputProps {
  /** Server truth. Replaces the draft when it changes while the field is idle. */
  value: string;
  /** Called at most once per finished edit, with the new value. */
  onCommit: (next: string) => void;
  id?: string;
  type?: string;
  style?: CSSProperties;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  'aria-label'?: string;
  'data-testid'?: string;
}

export function DraftTextInput({ value, onCommit, ...rest }: DraftTextInputProps) {
  const [draft, setDraft] = useState(value);
  const focused = useRef(false);
  // The value this field and its parent last agreed on. It moves on a resync
  // AND on a commit, which is what makes Enter followed by the blur it causes
  // one PATCH rather than two.
  const agreed = useRef(value);

  useEffect(() => {
    // Never stomp a field somebody is typing into — that is the #688 symptom in
    // its other direction. A change that arrives mid-edit is adopted on blur
    // instead (see below), if the draft was left untouched.
    if (focused.current) return;
    agreed.current = value;
    setDraft(value);
  }, [value]);

  const commit = (next: string) => {
    if (next === agreed.current) return;
    agreed.current = next;
    onCommit(next);
  };

  return (
    <input
      {...rest}
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onFocus={() => { focused.current = true; }}
      onBlur={() => {
        focused.current = false;
        if (draft !== agreed.current) commit(draft);
        // Untouched draft, but the prop moved while we held focus: take it now
        // rather than leaving a stale value on screen with no further prop
        // change to trigger the effect above.
        else if (draft !== value) { agreed.current = value; setDraft(value); }
      }}
      onKeyDown={e => {
        if (e.key === 'Enter') { e.preventDefault(); commit(draft); }
        else if (e.key === 'Escape') { e.preventDefault(); setDraft(agreed.current); }
      }}
    />
  );
}
